import { useQuery } from "@tanstack/react-query";
import { Activity, Clock, AlertCircle, TrendingUp, DollarSign, Cpu, Zap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getPerplexityMetrics } from "@/services/admin-api";
import { getPerplexityPromptLabel } from "./perplexityPromptLabels";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

export default function PerplexityMetrics() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["perplexity-metrics"],
    queryFn: getPerplexityMetrics,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-lg text-muted-foreground">Loading metrics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
          <p className="mt-4 text-lg font-medium">Failed to load metrics</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  const metrics = data?.data;

  if (!metrics) {
    return null;
  }

  const usage = metrics.usage;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Perplexity API Metrics"
        description="All Perplexity API usage across resume parsing, job parsing, and company research"
      />

      {/* All-time Usage Summary */}
      {usage && usage.allTime.totalCalls > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{usage.allTime.totalCalls.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {usage.recent.totalCalls} in last 60 min
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${usage.allTime.totalCost.toFixed(4)}</div>
              <p className="text-xs text-muted-foreground">
                ${usage.recent.totalCost.toFixed(4)} in last 60 min
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tokens Used</CardTitle>
              <Cpu className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(
                  usage.allTime.totalInputTokens + usage.allTime.totalOutputTokens
                ).toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                {usage.allTime.totalInputTokens.toLocaleString()} in /{" "}
                {usage.allTime.totalOutputTokens.toLocaleString()} out
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Latency</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(usage.allTime.avgLatencyMs / 1000).toFixed(1)}s
              </div>
              <p className="text-xs text-muted-foreground">
                {usage.allTime.successCalls} success / {usage.allTime.errorCalls} errors
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Per-Prompt Breakdown */}
      {usage && usage.byPrompt.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Usage by Call Type</CardTitle>
            <CardDescription>All-time breakdown by Perplexity call type</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Call Type</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Success</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Avg Latency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usage.byPrompt.map((row) => (
                  <TableRow key={row.promptId}>
                    <TableCell className="font-medium">
                      {getPerplexityPromptLabel(row.promptId)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.totalCalls}</TableCell>
                    <TableCell className="text-right tabular-nums text-green-600">
                      {row.successCalls}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">
                      {row.errorCalls}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {(row.totalInputTokens + row.totalOutputTokens).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      ${row.totalCost.toFixed(4)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {(row.avgLatencyMs / 1000).toFixed(1)}s
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* No usage message */}
      {(!usage || usage.allTime.totalCalls === 0) && (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              No Perplexity API calls recorded yet. Import a resume or job to see metrics here.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Company Research Queue Metrics */}
      <div>
        <h2 className="mb-4 text-xl font-semibold">Company Research Queue</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Real-time monitoring of the company research queue (last 60 minutes)
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average RPM</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.rpm.average}</div>
            <p className="text-xs text-muted-foreground">Peak: {metrics.rpm.peak} req/min</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.successRate}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.breakdown.successful} / {metrics.breakdown.total} requests
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics.breakdown.total > 0
                ? ((metrics.breakdown.failed / metrics.breakdown.total) * 100).toFixed(2)
                : "0.00"}
              %
            </div>
            <p className="text-xs text-muted-foreground">
              {metrics.breakdown.failed} failed requests
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Queue Depth</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.queueDepth}</div>
            <p className="text-xs text-muted-foreground">Queued + processing requests</p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Queue Breakdown */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Queue Request Breakdown</CardTitle>
            <CardDescription>Last 60 minutes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Total Requests</span>
                <span className="text-sm font-bold">{metrics.breakdown.total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-600">Successful</span>
                <span className="text-sm font-bold text-green-600">
                  {metrics.breakdown.successful}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-red-600">Failed</span>
                <span className="text-sm font-bold text-red-600">{metrics.breakdown.failed}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-yellow-600">Rate Limited (429)</span>
                <span className="text-sm font-bold text-yellow-600">{metrics.rateLimitHits}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Status</CardTitle>
            <CardDescription>Current state</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Monitoring Period</span>
                <span className="text-sm">{metrics.period.replace("_", " ")}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Circuit Breaker</span>
                {metrics.breakdown.failed > 10 ? (
                  <Badge variant="destructive">Open</Badge>
                ) : (
                  <Badge variant="success">Closed</Badge>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Auto Refresh</span>
                <span className="text-sm text-muted-foreground">Every 30s</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
